import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPartnerPage, partnerLeadSource, EMPLOYER_PRIVACY_LINE } from "@/config/partnerPages";
import { trackEvent } from "@/lib/analytics";

export const Route = createFileRoute("/p/$slug")({
  loader: ({ params }) => {
    const partner = getPartnerPage(params.slug);
    if (!partner) throw notFound();
    return { partner };
  },
  head: ({ loaderData }) => {
    const name = loaderData?.partner.name ?? "Partner";
    const title = `${name} members — Home Approach`;
    const description = `A free homebuying toolkit for ${name} members: down payment help in your ZIP, guidance, and expert introductions.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "noindex, nofollow" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary" },
      ],
    };
  },
  notFoundComponent: () => (
    <main className="container mx-auto px-4 py-20 text-center">
      <h1 className="text-2xl font-bold text-foreground">This partner page doesn't exist</h1>
      <Link to="/" className="mt-4 inline-block text-primary underline">Go to Home Approach</Link>
    </main>
  ),
  errorComponent: () => (
    <main className="container mx-auto px-4 py-20 text-center text-foreground">Something went wrong loading this page.</main>
  ),
  component: PartnerSignupPage,
});

function PartnerSignupPage() {
  const { partner } = Route.useLoaderData();
  const source = partnerLeadSource(partner.slug);
  const isEmployer = partner.kind === "employer";
  const employerParam = isEmployer && partner.employerName ? `&employer=${encodeURIComponent(partner.employerName)}` : "";
  const signupHref = `/auth?mode=signup&source=${encodeURIComponent(source)}${employerParam}`;

  useEffect(() => {
    trackEvent("partner_page_view", { partner: partner.slug });
  }, [partner.slug]);

  return (
    <main className="min-h-screen bg-background">
      <section className="container mx-auto max-w-2xl px-4 py-12 sm:py-16">
        <div className="mb-8 flex items-center gap-4">
          {partner.logoUrl && (
            <img src={partner.logoUrl} alt={`${partner.name} logo`} className="h-14 w-auto" />
          )}
          <span className="text-sm text-muted-foreground">{isEmployer ? `A benefit from ${partner.name}, powered by Home Approach` : "in partnership with Home Approach"}</span>
        </div>
        <h1 className="text-3xl font-bold leading-tight text-foreground sm:text-4xl">{partner.headline}</h1>
        <p className="mt-4 text-lg text-muted-foreground">{partner.intro}</p>
        <ul className="mt-8 space-y-3">
          {partner.perks.map((perk) => (
            <li key={perk} className="flex items-start gap-3 text-foreground">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
              <span>{perk}</span>
            </li>
          ))}
        </ul>
        {isEmployer && (
          <p className="mt-8 rounded-md border border-border bg-muted p-4 text-sm text-foreground">{EMPLOYER_PRIVACY_LINE}</p>
        )}
        <Button asChild size="lg" className="mt-10 w-full sm:w-auto">
          <a href={signupHref} onClick={() => trackEvent("partner_page_cta", { partner: partner.slug })}>
            Start your journey today
          </a>
        </Button>
        <p className="mt-8 text-xs text-muted-foreground">
          Home Approach and {partner.name} are separate companies. Home Approach is paid only by employers; no provider pays us. You're never
          required to use any provider, and your choice doesn't affect your access to our tools. Not a loan offer.{" "}
          <Link to="/how-we-make-money" className="underline">How we make money</Link>
        </p>
      </section>
    </main>
  );
}
